import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

/**
 * Unified delete confirmation hook with double-click pattern.
 * Replaces 6+ implementations of the "click once to arm, click again to confirm" pattern.
 * 
 * Usage:
 * const { 
 *   pendingDeleteId, 
 *   handleDeleteClick, 
 *   isDeleting,
 *   isPendingConfirm 
 * } = useDeleteConfirmation({
 *   entityType: 'Artifact',
 *   queryKey: 'artifacts',
 *   confirmTimeout: 3000
 * });
 * 
 * <Button onClick={() => handleDeleteClick(item.id)}>
 *   {isPendingConfirm(item.id) ? 'Confirm?' : 'Delete'}
 * </Button>
 */

export default function useDeleteConfirmation({
  entityType,
  queryKey,
  onSuccess,
  onError,
  confirmTimeout = 3000
}) {
  const queryClient = useQueryClient();
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Auto-clear pending confirmation after timeout
  useEffect(() => {
    if (pendingDeleteId) {
      const timer = setTimeout(() => {
        setPendingDeleteId(null);
      }, confirmTimeout);
      return () => clearTimeout(timer);
    }
  }, [pendingDeleteId, confirmTimeout]);

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities[entityType].delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setPendingDeleteId(null);
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to delete ${entityType}:`, error);
      setPendingDeleteId(null);
      onError?.(error);
    }
  });

  const handleDeleteClick = useCallback((id) => {
    if (pendingDeleteId === id) {
      // Second click - confirm delete
      deleteMutation.mutate(id);
    } else {
      // First click - arm for deletion
      setPendingDeleteId(id);
    }
  }, [pendingDeleteId, deleteMutation]);

  const cancelPendingDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const isPendingConfirm = useCallback((id) => {
    return pendingDeleteId === id;
  }, [pendingDeleteId]);

  return {
    pendingDeleteId,
    handleDeleteClick,
    cancelPendingDelete,
    isPendingConfirm,
    isDeleting: deleteMutation.isPending,
    deletingId: deleteMutation.isPending ? pendingDeleteId : null,
    error: deleteMutation.error
  };
}

/**
 * Simple state-only version (when you handle the actual delete elsewhere)
 */
export function useDeleteConfirmState({ confirmTimeout = 3000 } = {}) {
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    if (pendingDeleteId) {
      const timer = setTimeout(() => {
        setPendingDeleteId(null);
      }, confirmTimeout);
      return () => clearTimeout(timer);
    }
  }, [pendingDeleteId, confirmTimeout]);

  const armDelete = useCallback((id) => {
    setPendingDeleteId(id);
  }, []);

  const clearPending = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const isPendingConfirm = useCallback((id) => {
    return pendingDeleteId === id;
  }, [pendingDeleteId]);

  const checkAndConfirm = useCallback((id, onConfirm) => {
    if (pendingDeleteId === id) {
      setPendingDeleteId(null);
      onConfirm(id);
      return true;
    } else {
      setPendingDeleteId(id);
      return false;
    }
  }, [pendingDeleteId]);

  return {
    pendingDeleteId,
    armDelete,
    clearPending,
    isPendingConfirm,
    checkAndConfirm
  };
}